import { CommonModule, ViewportScroller } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, effect, ElementRef, inject, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { isDocumentLineItemMissingStockBacking, isContractDocumentDefinition } from '@phoenix/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import { I18nService } from '../../../../@core/services/i18n/i18n.service';
import { FormValidationStatus } from '../../../../@core/types/form-validation-status.type';
import { BaseDetailComponent, IFormState } from '../../../../common/base-detail.component';
import { Document, DocumentLineItem, DocumentLineItemType, DocumentStatusType, ProductType } from '../../../../generated/generated-types';
import { FILL_HEIGHT_LIST_HOST_CLASS, FILL_HEIGHT_LIST_MEASURED_HOST_CLASS } from '../../../../shared/layout/page-fill-host.classes';
import { LocaleDatePipe } from '../../../../shared/pipes/locale-date.pipe';
import { FormlyFillHeightListHostDirective } from '../../../custom-formly/formly-tabs/formly-fill-height-list-host.directive';
import { DocumentService } from '../../services/document.service';
import { WidgetAddEditLineItemListComponent } from '../widget-add-edit-document-line-item/widget-add-edit-line-item-list.component';
import { WidgetEditGenericDocumentComponent } from '../widget-edit-generic-document/widget-edit-generic-document.component';
import { WidgetEditContractDocumentComponent } from '../widget-edit-contract-document/widget-edit-contract-document.component';
import { IFormlyAddEditLineItemListComponent } from './formly-add-edit-line-item-list.interface';

@Component({
  selector: 'app-formly-add-edit-line-item-list',
  host: {
    class: FILL_HEIGHT_LIST_MEASURED_HOST_CLASS,
  },
  hostDirectives: [FormlyFillHeightListHostDirective],
  templateUrl: './formly-add-edit-line-item-list.component.html',
  standalone: true,
  providers: [
    LocaleDatePipe,
  ],
  imports: [
    WidgetAddEditLineItemListComponent,
    BlockableDiv,
    ButtonModule,
    TooltipModule,
    TranslatePipe,
    CommonModule,
  ]
})
export class FormlyAddEditLineItemListComponent extends FieldType<FieldTypeConfig> implements AfterViewInit, IFormlyAddEditLineItemListComponent {

  readonly flexClass = 'flex min-h-0 flex-1 flex-col w-full overflow-hidden';
  readonly listWidgetClass = FILL_HEIGHT_LIST_HOST_CLASS;

  isSaving = false;

  cachedLineItems: DocumentLineItem[] = []
  firstLoad: boolean = true;

  destroyRef = inject(DestroyRef);

  dataTestId = signal<string>('formly-document-line-items-grid-list');

  addEditLineItemWidget = viewChild<WidgetAddEditLineItemListComponent>('addEditLineItemWidget');

  constructor(
    private docService: DocumentService,
    private cdRef: ChangeDetectorRef,
    private scroller: ViewportScroller,
    private elementRef: ElementRef,
    private i18n: I18nService,
  ) {
    super();

    // Had weird behavior in e2e where test ids where either "addNewItemList" or "addNewItemList-formly-document-line-items-grid-list"
    // afterNextRender({
    //   mixedReadWrite: () => {
    //     this.formControl.valueChanges.subscribe((lineItems: DocumentLineItem[]) => {
    //       this.dataTestId.set('formly-document-line-items-grid-list');
    //     });
    //   }
    // },);

    // direct binding doesnt work - since it would be too late, so only like this works
    effect(() => {
      if (this.addEditLineItemWidget?.()?.inEditingSig() !== undefined) {
        untracked(() => {
          if (this.formState?.detailComponent) {
            this.formState.detailComponent.actionBarDisabledSig.set(this.addEditLineItemWidget().inEditingSig());
            this.formState.detailComponent.lineItemInEditingSig.set(this.addEditLineItemWidget().inEditingSig());
            this.formState.detailComponent.listButtonDisabledSig.set(this.addEditLineItemWidget().inEditingSig());
            this.formState.detailComponent.copyButtonDisabledSig.set(this.addEditLineItemWidget().inEditingSig());
            this.formState.detailComponent.addNewButtonDisabledSig.set(this.addEditLineItemWidget().inEditingSig());
          }
        })
      }
    })

    effect(() => {
      const items = this.addEditLineItemWidget?.()?.itemsSig();
      if (!items)
        return;
      if (items.some(x => x?.product?.productType === ProductType.STORE && isDocumentLineItemMissingStockBacking(x?.quantity, x?.lineItemQuantityStatus?.quantityBackedByStock, x.document?.documentDefinition?.stockAction)))
        untracked(() => this.formState.detailComponent?.lineItemsNotAllBackedByStock.set(true));
      else
        untracked(() => this.formState.detailComponent?.lineItemsNotAllBackedByStock.set(false));

      if (items.some(x => x?.type === DocumentLineItemType.SBOMH || x?.type === DocumentLineItemType.SHIPPING)) {
        untracked(() => {
          const detail = this.formState.detailComponent as WidgetEditGenericDocumentComponent | undefined;
          detail?.setSbomhOrShippingLineItemsPresent(true);
        });
      }
    })
  }

  ngOnInit() {
    this.options?.formState?.detailComponent?.needsDliWidgetRefresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (x: boolean) => {
      if (x)
        await this.updateDLIsAndDoc();
      //refresh Document after recalculating
    });

    this.options.formState.dliListComponent = this;
  }

  async ngAfterViewInit() {

    let component = this.options.formState.detailComponent as WidgetEditGenericDocumentComponent;

    if (!component) {
      console.error("No detail component found to save DLI");
      return;
    }

    if (component.initialProductId) {

      if (component.modelIsDirtySig())
        await this.saveToUnlockEditMode();

      //set timeout 2000ms
      await new Promise(resolve => setTimeout(resolve, 2000)); //TODO by event

      if (component.initialProductId) {
        console.log("Adding initial product to DLI");
        this.addEditLineItemWidget().addNewItem(null, { productId: component.initialProductId } as DocumentLineItem);
        this.cdRef.detectChanges();
        //TODO scroll into view
      }
    }

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          continue;
        }
        const detailWidget = this.formState.detailComponent as WidgetEditGenericDocumentComponent;
        if (
          !detailWidget?.detailModel?.id
          || detailWidget.saving()
          || detailWidget.loading()
          || !detailWidget.afterViewInitComplete()
          || detailWidget.formIsBuilding()
        ) {
          return;
        }
        if (detailWidget.modelIsDirtySig() && detailWidget.detailForm.status === FormValidationStatus.VALID) {
          detailWidget.save();
        }
        observer.disconnect();
      }
    });

    observer.observe(this.elementRef.nativeElement);

    this.destroyRef.onDestroy(() => {
      delete this.options?.formState?.dliListComponent
      observer.disconnect();
    });

  }

  async updateDLIsAndDoc() {
    // recalculation can not be done in frontend otherwise its incomplete - so just refresh and logic in backend
    if (this.addEditLineItemWidget?.())
      await this.addEditLineItemWidget().refresh();
    else
      console.debug("No addEditLineItemWidget found to update DLI");
  }

  checkIfBlocked() {

    let model = this.model as Document;

    if (!model)
      return false;

    const detailComponent = this.options?.formState?.detailComponent as BaseDetailComponent<Document>;
    if (detailComponent?.loading?.())
      return false;

    if (!model.id)
      return true;

    if (model.status == DocumentStatusType.CO || model.status == DocumentStatusType.RE) //cant save when completed or released
      return false;

    let dirty = (this.options.formState as IFormState).isDirty == true;
    return dirty;
  }

  async saveToUnlockEditMode() {

    //Other options would be to use the service, but special overwrite in the component wont be used then

    let component = this.options.formState.detailComponent as BaseDetailComponent<Document>;

    if (!component) {
      console.error("No detail component found to save DLI");
      return;
    }

    this.isSaving = true;
    this.cdRef.detectChanges();

    await component.save();

    this.isSaving = false;
    this.cdRef.detectChanges();
  }

  updateErrorCheckInDocument() {
    // This is being called many times, not sure if its the right behavior.
    if (this.field.options.formState?.detailComponent?.checkForWarningsAndErrors) {
      this.field.options.formState.detailComponent.checkForWarningsAndErrors();
    }
  }

  onItemsChanged() {
    this.updateErrorCheckInDocument();
    const detail = this.options.formState?.detailComponent as WidgetEditGenericDocumentComponent | undefined;
    detail?.packingConfigLineItemsChanged$?.next();
  }

  isContractDocumentEditor(): boolean {
    const model = this.model as Document;
    return isContractDocumentDefinition(model?.documentDefinition, model);
  }

  private contractDetailComponent(): WidgetEditContractDocumentComponent | null {
    const detail = this.options?.formState?.detailComponent;
    return detail instanceof WidgetEditContractDocumentComponent ? detail : null;
  }

  contractGenerateDisabled(): boolean {
    const selected = this.addEditLineItemWidget()?.selectedItems?.() ?? [];
    if (selected.length === 0) {
      return true;
    }
    const detail = this.contractDetailComponent();
    return (detail?.disabledGenerateContractDocumentsSig() ?? true)
      || (detail?.generatingContractDocumentsSig() ?? false);
  }

  contractGenerateLoading(): boolean {
    return this.contractDetailComponent()?.generatingContractDocumentsSig() ?? false;
  }

  contractGenerateTooltip(): string | null {
    const selected = this.addEditLineItemWidget()?.selectedItems?.() ?? [];
    if (selected.length === 0) {
      return this.i18n.translate(_('common.no-selected-items'));
    }
    return this.contractDetailComponent()?.generateContractDocumentsTooltipSig() ?? null;
  }

  contractSaveLineItemsWithStockErrors(): boolean {
    return this.contractDetailComponent()?.saveLineItemsWithStockErrors() ?? false;
  }

  onContractGenerateClick(): void {
    const selected = this.addEditLineItemWidget()?.selectedItems?.() ?? [];
    void this.contractDetailComponent()?.generateSelectedContractLines(selected);
  }

  onContractSaveLineItemsWithStockErrorsChange(enabled: boolean): void {
    this.contractDetailComponent()?.saveLineItemsWithStockErrors.set(enabled);
  }

}
